import 'isomorphic-unfetch';
import { differenceInHours } from 'date-fns';
import { get, set } from 'idb-keyval';
import { customAlphabet } from 'nanoid';

const emptySetHeaders = () => {
};
class MailroomClient {
  constructor({ basepath, setHeaders }) {
    this.lastSessionHash = "";
    this._basepath = basepath;
    this.sessionId = null;
    this.metadata = null;
    this.user = null;
    this.setRequestHeaders = setHeaders !== null && setHeaders !== void 0 ? setHeaders : emptySetHeaders;
    this.lastSessionHash = "";
  }
  /**
   * Sets the current session ID in the Mailroom instance store to be injected
   * in to the Mailroom dispatch payload.
   */
  async setSessionId(id) {
    this.sessionId = id;
    this.setRequestHeaders(this.getHeaders());
    await this.handleSessionUpdate();
  }
  /**
   * Sets the current custom metadata passed into Proppsland via the Propps
   * script. This metadata is usually set by external developers and we attach
   * them to each event so that we can return the data back to them via
   * Webhooks.
   */
  async setMetadata(metadata) {
    this.metadata = metadata;
    this.setRequestHeaders(this.getHeaders());
    await this.handleSessionUpdate();
  }
  async handleSessionUpdate() {
    const hash = [this.metadata, this.sessionId].join(":");
    if (this.metadata && this.sessionId && this.lastSessionHash !== hash) {
      await fetch(`${this._basepath}/session/init`, {
        body: JSON.stringify({
          sessionId: this.sessionId,
          data: { metadata: this.metadata, started: (/* @__PURE__ */ new Date()).toISOString() }
        }),
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }).catch((err) => {
        console.error("[Mailroom \u{1F4EE}] Error with initialising session.", err);
      });
      this.lastSessionHash = hash;
    }
  }
  setUser(user) {
    this.user = user;
  }
  clearUser() {
    this.user = null;
  }
  clearStore() {
    this.sessionId = null;
    this.metadata = null;
    this.user = null;
    this.setRequestHeaders(this.getHeaders());
  }
  async dispatch(payload) {
    const fullPayload = Object.assign(Object.assign({}, payload), { user: this.user, sessionId: this.sessionId, metadata: {
      custom: this.metadata
    } });
    await fetch(`${this._basepath}/pixel/${payload.topic}`, {
      body: JSON.stringify(fullPayload),
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      }
    }).then((res) => res.json()).catch((err) => {
      console.error("[Mailroom \u{1F4EE}] Error with dispatching event to API.", err);
    });
  }
  getHeaders() {
    const headers = {
      "x-pixel-session-id": void 0,
      "x-pixel-metadata": void 0
    };
    if (this.sessionId) {
      headers["x-pixel-session-id"] = this.sessionId;
    }
    if (this.metadata) {
      headers["x-pixel-metadata"] = this.metadata;
    }
    return headers;
  }
}

const BASE_62 = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const base62_custom = (size, prefix) => {
  const generateId = customAlphabet(BASE_62, size);
  if (prefix) {
    return () => prefix + generateId();
  }
  return generateId;
};

class PixelBaseClient {
  constructor(options) {
    this.mailroom = new MailroomClient({
      basepath: options.mailroom.basePath,
      setHeaders: options.mailroom.setRequestHeaders
    });
    this.userId = null;
    this.user = null;
    this.configs = options.configs;
    this._session = null;
  }
  async initialise() {
    if (!this._session) {
      await this.getSessionId();
    }
  }
  /**
   * Set internal Pixel Client class userId. Is not necessarily transmitted.
   * @private
   */
  _setUserId(uid) {
    this.userId = uid;
    if (!this.user || this.user.uid !== uid) {
      this.user = { uid, role: null };
    }
  }
  /**
   * Set internal Pixel user which is injected to Mailroom events.
   * @private
   */
  _setUser(user) {
    this.user = user;
    this.mailroom.setUser(user);
  }
  /**
   * Clears the pixel user to no longer transmit user information to mailroom events.
   * @private
   */
  _clearUser() {
    this.mailroom.clearUser();
    this.user = null;
    this.userId = null;
  }
  /**
   * Dispatch an event to the mailroom API to broadcast through the Propps internal
   * pub/sub system.
   * @param topic the mailroom topic to dispatch to
   * @param eventName the event name within the topic
   * @param event event payload
   */
  async dispatchMailroom(topic, name, user, data) {
    const sid = await this.getSessionId();
    this.mailroom.setSessionId(sid);
    await this.mailroom.dispatch({
      topic,
      event: name,
      id: generateId("evt_"),
      data: {
        type: name,
        cat: /* @__PURE__ */ new Date(),
        sid,
        user: user ?? this.user,
        ...data
      }
    });
    await this.keepSessionAlive();
  }
  get sessionId() {
    var _a;
    return (_a = this._session) == null ? void 0 : _a.id;
  }
  async setCustomMetadata(metadata) {
    return this.mailroom.setMetadata(metadata);
  }
  async keepSessionAlive() {
    if (!this._session) {
      return;
    }
    this._session = { ...this._session, lastAt: /* @__PURE__ */ new Date() };
    await this.handleSessionChange();
  }
  async setSessionId(id) {
    this._session = { id, startedAt: /* @__PURE__ */ new Date(), lastAt: /* @__PURE__ */ new Date() };
    await this.handleSessionChange();
    return id;
  }
  async getSessionId() {
    const now = /* @__PURE__ */ new Date();
    const storedSession = await this.getSession();
    if (storedSession) {
      if (differenceInHours(now, storedSession.lastAt) < 24) {
        return storedSession.id;
      }
    }
    const newSession = await this.createSession();
    return newSession.id;
  }
  /**
   * Fetches session data from instance or local persistent storage if it exists.
   * @returns Session data or null if not in storage
   */
  async getSession() {
    if (this._session) {
      return this._session;
    }
    try {
      const data = await get("__pixel_session");
      return data ?? null;
    } catch (e) {
      console.error("[Propps Pixel] Could not retrieve session data.", e);
      return null;
    }
  }
  /**
   * Create a new session and store session info into persistent storage
   * @returns Session data
   */
  async createSession() {
    const now = /* @__PURE__ */ new Date();
    const sessionId = generateId("ses_");
    const session = {
      id: sessionId,
      lastAt: now,
      startedAt: now
    };
    this._session = session;
    await this.handleSessionChange();
    return session;
  }
  async handleSessionChange() {
    if (this._session) {
      await set("__pixel_session", this._session).catch(
        (e) => console.error("[Propps Pixel] Could not store session data.", e)
      );
      await this.mailroom.setSessionId(this._session.id);
    }
  }
}
const generateId = (prefix) => base62_custom(32, prefix)();

class PixelFrontendClient extends PixelBaseClient {
  constructor(options) {
    super({ mailroom: options.mailroom, configs: options.configs });
    /** Amplitude client */
    this.amplitude = null;
    if (options.amplitude) {
      this.amplitude = options.amplitude;
    }
  }
  /**
   * Dispatches an analytics event to the Propps Mailroom and Amplitude based on
   * the event configuration.
   * @param event payload
   */
  async dispatch(event) {
    if (!(event.type in this.configs)) {
      console.error(
        `[Propps Pixel] Config for event: \`${event.type}\` was not provided. Event dispatch will be skipped.`
      );
      return;
    }
    const { mailroom, amplitude } = this.configs[event.type];
    const payload = omit(event, "type");
    if (mailroom.dispatch) {
      this.dispatchMailroom(mailroom.topic, event.type, this.user, event);
    }
    if (amplitude.dispatch) {
      this.dispatchAmplitude(amplitude.event, payload);
    }
  }
  /**
   * Clears the current user context passed along through Mailroom and Amplitude
   * events.
   */
  clearUser() {
    this._clearUser();
    this.setUserId(null);
  }
  /**
   * Sets the session user id.
   * @param uid user id
   */
  async setUserId(uid) {
    var _a;
    this._setUserId(uid);
    (_a = this.amplitude) == null ? void 0 : _a.setUserId(uid);
  }
  /**
   * Sets a user for the current session. The `user.uid` will be forwarded to
   * Amplitude + LD and the `user` object injected to mailroom events. Amplitude
   * + LD user properties will be set using role data.
   * @param user User details
   */
  async setUser(user) {
    var _a, _b;
    this.clearUser();
    if (user.uid) {
      this._setUser(user);
      this.setUserId(user.uid);
    }
    if (user.role && user.uid) {
      if (user.role.id && user.role.type) {
        (_a = this.amplitude) == null ? void 0 : _a.setUserProperties({
          roleId: user.role.id,
          role: user.role.type
        });
      } else {
        (_b = this.amplitude) == null ? void 0 : _b.setUserProperties({ roleId: null, role: null });
      }
    }
  }
  /**
   * Dispatches an Amplitude event using the `amplitude-js` library.
   * @param name name of the amplitude event
   * @param event payload
   */
  async dispatchAmplitude(name, event) {
    var _a;
    (_a = this.amplitude) == null ? void 0 : _a.logEvent(name, event);
  }
}
const omit = (obj, ...keys) => {
  const ret = {};
  let key;
  for (key in obj) {
    if (!keys.includes(key)) {
      ret[key] = obj[key];
    }
  }
  return ret;
};

export { PixelFrontendClient };
